import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col, Button, Card, Badge, Accordion } from 'react-bootstrap';
import './LandingPage.css';

const LandingPage = () => {
  const [isVisible, setIsVisible] = useState({});
  const [activeFeature, setActiveFeature] = useState(0);
  const [typedText, setTypedText] = useState('');
  const fullText = 'meetings into actionable insights';
  
  // Typing animation effect
  useEffect(() => {
    let index = 0;
    const timer = setInterval(() => {
      if (index <= fullText.length) {
        setTypedText(fullText.slice(0, index));
        index++;
      } else {
        clearInterval(timer);
      }
    }, 50);
    return () => clearInterval(timer);
  }, []);

  // Intersection observer for scroll animations
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('.animate-on-scroll').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const timer = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 5);
    }, 4000);
    return () => clearInterval(timer);
  }, []);

  const features = [
    {
      icon: '🎙️',
      title: 'AI Transcription',
      description: 'Advanced AI transcription with 99% accuracy across 50+ languages',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    },
    {
      icon: '⚡',
      title: 'Instant Summaries',
      description: 'AI generates executive summaries in seconds, not hours',
      gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
    },
    {
      icon: '✅',
      title: 'Action Items',
      description: 'Automatically extract tasks, deadlines, and assignees',
      gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
    },
    {
      icon: '💬',
      title: 'AI Chat',
      description: 'Ask questions about any meeting - generate emails, find quotes, get insights',
      gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
    },
    {
      icon: '📄',
      title: 'Document Analysis',
      description: 'Upload PDFs, contracts, and documents - chat with AI to extract insights instantly',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #ef4444 100%)'
    }
  ];

  const testimonials = [
    {
      quote: "EchoBrief cut our meeting follow-up time by 80%. Game changer for our sales team.",
      author: "Sarah Chen",
      role: "VP of Sales, TechCorp",
      avatar: "SC"
    },
    {
      quote: "Finally, a tool that actually understands context. The AI summaries are incredibly accurate.",
      author: "Marcus Johnson",
      role: "Product Manager, StartupXYZ",
      avatar: "MJ"
    },
    {
      quote: "We've processed 500+ meetings. The ROI is undeniable - it's like having a personal assistant.",
      author: "Emily Rodriguez",
      role: "CEO, ConsultingPro",
      avatar: "ER"
    }
  ];

  const stats = [
    { value: '10M+', label: 'Minutes Transcribed' },
    { value: '50K+', label: 'Meetings Processed' },
    { value: '99%', label: 'Accuracy Rate' },
    { value: '4.9', label: 'User Rating' }
  ];

  const faqs = [
    {
      question: 'How accurate is the transcription?',
      answer: 'EchoBrief uses advanced AI transcription, achieving 99%+ accuracy for clear audio. We support 50+ languages and handle multiple speakers with ease.'
    },
    {
      question: 'Is my data secure?',
      answer: 'Absolutely. All data is encrypted at rest and in transit. We\'re SOC 2 compliant and never use your data to train AI models. You can delete your data anytime.'
    },
    {
      question: 'Can I integrate with my existing tools?',
      answer: 'Yes! We integrate with Zoom, Microsoft Teams, Google Meet, Slack, Salesforce, HubSpot, and more. API access is available on Pro and Enterprise plans.'
    },
    {
      question: 'How long does processing take?',
      answer: 'Most meetings are processed in under 5 minutes. A 1-hour meeting typically takes 2-3 minutes for full transcription and AI analysis.'
    },
    {
      question: 'Can I try before I buy?',
      answer: 'Yes! Our free plan includes 5 meetings per month with full features. No credit card required to start.'
    }
  ];

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="landing-nav">
        <Container>
          <div className="nav-content">
            <div className="logo">
              <span className="logo-icon">📊</span>
              <span className="logo-text">EchoBrief</span>
            </div>
            <div className="nav-links">
              <a href="#features">Features</a>
              <a href="#how-it-works">How it Works</a>
              <a href="#pricing">Pricing</a>
              <a href="#faq">FAQ</a>
            </div>
            <div className="nav-cta">
              <Link to="/login" className="btn-login">Log In</Link>
              <Link to="/register" className="btn-signup">Start Free</Link>
            </div>
          </div>
        </Container>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-bg-animation">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
          <div className="gradient-orb orb-3"></div>
        </div>
        <Container>
          <Row className="align-items-center min-vh-100">
            <Col lg={6} className="hero-content">
              <Badge className="hero-badge">
                <span className="badge-dot"></span>
                AI-Powered Meeting Intelligence
              </Badge>
              <h1 className="hero-title">
                Transform your <br/>
                <span className="gradient-text">{typedText}</span>
                <span className="cursor">|</span>
              </h1>
              <p className="hero-subtitle">
                Stop taking notes. Start taking action. EchoBrief automatically transcribes, 
                summarizes, and extracts action items from every meeting—so you can focus on what matters.
              </p>
              <div className="hero-cta">
                <Link to="/register" className="btn-primary-cta">
                  Get Started Free
                  <span className="btn-arrow">→</span>
                </Link>
                <button className="btn-secondary-cta">
                  <span className="play-icon">▶</span>
                  Watch Demo
                </button>
              </div>
              <div className="hero-trust">
                <span className="trust-text">Trusted by teams at</span>
                <div className="trust-logos">
                  <span className="trust-logo">Google</span>
                  <span className="trust-logo">Microsoft</span>
                  <span className="trust-logo">Stripe</span>
                  <span className="trust-logo">Shopify</span>
                </div>
              </div>
              
              <div className="powered-by">
                <span className="powered-text">Powered by</span>
                <div className="powered-logos">
                  <div className="powered-item">
                    <span className="powered-icon">🤖</span>
                    <span>Advanced AI</span>
                  </div>
                  <div className="powered-item">
                    <span className="powered-icon">🎤</span>
                    <span>Speech AI</span>
                  </div>
                  <div className="powered-item">
                    <span className="powered-icon">👁️</span>
                    <span>Vision AI</span>
                  </div>
                </div>
              </div>
            </Col>
            <Col lg={6} className="hero-visual">
              <div className="dashboard-preview">
                <div className="preview-header">
                  <div className="preview-dots">
                    <span></span><span></span><span></span>
                  </div>
                  <span className="preview-title">Meeting Summary</span>
                </div>
                <div className="preview-content">
                  <div className="preview-card animate-float">
                    <div className="preview-icon">📝</div>
                    <div className="preview-text">
                      <strong>Executive Summary</strong>
                      <p>Q4 planning meeting covered budget allocation, hiring goals, and product roadmap priorities...</p>
                    </div>
                  </div>
                  <div className="preview-card animate-float-delayed">
                    <div className="preview-icon">✅</div>
                    <div className="preview-text">
                      <strong>3 Action Items Extracted</strong>
                      <p>• Sarah to finalize budget by Friday<br/>• Mike to schedule design review<br/>• Team to submit OKRs</p>
                    </div>
                  </div>
                  <div className="preview-card animate-float-more-delayed">
                    <div className="preview-icon">💬</div>
                    <div className="preview-text">
                      <strong>AI Chat Ready</strong>
                      <p>"What did John say about the timeline?"</p>
                    </div>
                  </div>
                  <div className="preview-card new-feature animate-float">
                    <div className="preview-icon">📄</div>
                    <div className="preview-text">
                      <strong>Document Analysis <span className="new-tag">NEW</span></strong>
                      <p>Upload contracts & PDFs, chat with AI to extract insights</p>
                    </div>
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
        <div className="scroll-indicator">
          <span>Scroll to explore</span>
          <div className="scroll-arrow"></div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <Container>
          <Row>
            {stats.map((stat, index) => (
              <Col md={3} sm={6} key={index} className="stat-item">
                <div className="stat-value">{stat.value}</div>
                <div className="stat-label">{stat.label}</div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Capabilities Showcase - New Advanced Section */}
      <section className="capabilities-section">
        <Container>
          <div className="section-header">
            <Badge className="section-badge">Capabilities</Badge>
            <h2 className="section-title">One platform.<br/><span className="gradient-text">Infinite possibilities.</span></h2>
          </div>
          
          <div className="capabilities-grid">
            <div className="capability-card large">
              <div className="capability-glow"></div>
              <div className="capability-icon">🎯</div>
              <h3>Meeting Intelligence</h3>
              <p>Transform hours of recordings into actionable insights. Our AI understands context, identifies speakers, and extracts what matters.</p>
              <div className="capability-features">
                <span>Multi-speaker detection</span>
                <span>Sentiment analysis</span>
                <span>Topic clustering</span>
              </div>
            </div>
            
            <div className="capability-card">
              <div className="capability-glow"></div>
              <div className="capability-icon">📑</div>
              <h3>Document Analysis</h3>
              <p>Upload contracts, reports, and PDFs. Chat with your documents to find answers instantly.</p>
              <div className="capability-badge-new">NEW</div>
            </div>
            
            <div className="capability-card">
              <div className="capability-glow"></div>
              <div className="capability-icon">🔗</div>
              <h3>Slack Integration</h3>
              <p>Send summaries, action items, and insights directly to your team's Slack channels.</p>
            </div>
            
            <div className="capability-card">
              <div className="capability-glow"></div>
              <div className="capability-icon">👥</div>
              <h3>People Analytics</h3>
              <p>Track participation, speaking time, and engagement across all your meetings.</p>
            </div>
            
            <div className="capability-card">
              <div className="capability-glow"></div>
              <div className="capability-icon">📊</div>
              <h3>Visual Analytics</h3>
              <p>Beautiful dashboards showing meeting trends, productivity metrics, and team insights.</p>
            </div>
          </div>
        </Container>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section animate-on-scroll">
        <Container>
          <div className="section-header">
            <Badge className="section-badge">Features</Badge>
            <h2 className="section-title">Everything you need to<br/><span className="gradient-text">master your meetings</span></h2>
            <p className="section-subtitle">Powerful AI tools that work together seamlessly</p>
          </div>
          
          <Row className="features-showcase">
            <Col lg={5}>
              <div className="features-list">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className={`feature-item ${activeFeature === index ? 'active' : ''}`}
                    onClick={() => setActiveFeature(index)}
                    style={{ '--feature-gradient': feature.gradient }}
                  >
                    <div className="feature-icon">{feature.icon}</div>
                    <div className="feature-content">
                      <h3>{feature.title}</h3>
                      <p>{feature.description}</p>
                    </div>
                    <div className="feature-indicator"></div>
                  </div>
                ))}
              </div>
            </Col>
            <Col lg={7}>
              <div className="feature-demo" style={{ background: features[activeFeature].gradient }}>
                <div className="demo-window">
                  <div className="demo-header">
                    <div className="demo-dots"><span></span><span></span><span></span></div>
                  </div>
                  <div className="demo-content">
                    {activeFeature === 0 && (
                      <div className="demo-transcription">
                        <div className="transcript-line"><span className="speaker">Sarah:</span> Let's discuss the Q4 roadmap...</div>
                        <div className="transcript-line"><span className="speaker">Mike:</span> I think we should prioritize the mobile app...</div>
                        <div className="transcript-line typing"><span className="speaker">John:</span> The timeline looks aggressive but achievable...</div>
                      </div>
                    )}
                    {activeFeature === 1 && (
                      <div className="demo-summary">
                        <h4>Executive Summary</h4>
                        <p>The team discussed Q4 priorities, focusing on mobile app development and infrastructure improvements. Key decisions include allocating 60% of resources to mobile and hiring 3 new engineers.</p>
                        <div className="summary-tags">
                          <span>Q4 Planning</span>
                          <span>Mobile</span>
                          <span>Hiring</span>
                        </div>
                      </div>
                    )}
                    {activeFeature === 2 && (
                      <div className="demo-actions">
                        <div className="action-item">
                          <span className="action-check">☐</span>
                          <span className="action-text">Finalize mobile app wireframes</span>
                          <span className="action-assignee">@sarah</span>
                          <span className="action-date">Dec 15</span>
                        </div>
                        <div className="action-item">
                          <span className="action-check">☐</span>
                          <span className="action-text">Schedule engineering interviews</span>
                          <span className="action-assignee">@mike</span>
                          <span className="action-date">Dec 10</span>
                        </div>
                        <div className="action-item">
                          <span className="action-check">☑</span>
                          <span className="action-text completed">Send meeting recap to stakeholders</span>
                          <span className="action-assignee">@john</span>
                          <span className="action-date">Today</span>
                        </div>
                      </div>
                    )}
                    {activeFeature === 3 && (
                      <div className="demo-chat">
                        <div className="chat-message user">
                          <p>What did Sarah say about the budget?</p>
                        </div>
                        <div className="chat-message ai">
                          <p>Sarah mentioned that the Q4 budget is $2.5M, with 60% allocated to mobile development and 25% to infrastructure. She emphasized staying within budget while meeting the December deadline.</p>
                        </div>
                        <div className="chat-input">
                          <input type="text" placeholder="Ask anything about this meeting..." />
                          <button>→</button>
                        </div>
                      </div>
                    )}
                    {activeFeature === 4 && (
                      <div className="demo-documents">
                        <div className="doc-upload-area">
                          <div className="doc-icon">📄</div>
                          <span>Contract_2024.pdf</span>
                          <span className="doc-status">✓ Analyzed</span>
                        </div>
                        <div className="doc-chat">
                          <div className="chat-message user">
                            <p>What are the termination clauses?</p>
                          </div>
                          <div className="chat-message ai">
                            <p><strong>Section 8.2:</strong> Either party may terminate with 30 days written notice. Early termination incurs a 15% fee of remaining contract value...</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="how-it-works-section animate-on-scroll">
        <Container>
          <div className="section-header">
            <Badge className="section-badge">How It Works</Badge>
            <h2 className="section-title">From recording to insights<br/><span className="gradient-text">in 3 simple steps</span></h2>
          </div>
          
          <div className="steps-container">
            <div className="steps-line"></div>
            <Row>
              <Col md={4}>
                <div className="step-card">
                  <div className="step-number">1</div>
                  <div className="step-icon">📤</div>
                  <h3>Upload or Record</h3>
                  <p>Upload any audio/video file or record directly in your browser. We support all major formats and integrations.</p>
                </div>
              </Col>
              <Col md={4}>
                <div className="step-card">
                  <div className="step-number">2</div>
                  <div className="step-icon">🤖</div>
                  <h3>AI Processing</h3>
                  <p>Our AI transcribes, analyzes, and extracts key information automatically. Usually done in under 5 minutes.</p>
                </div>
              </Col>
              <Col md={4}>
                <div className="step-card">
                  <div className="step-number">3</div>
                  <div className="step-icon">🚀</div>
                  <h3>Take Action</h3>
                  <p>Review summaries, assign tasks, chat with AI about the meeting, and share insights with your team.</p>
                </div>
              </Col>
            </Row>
          </div>
        </Container>
      </section>

      {/* Testimonials */}
      <section className="testimonials-section animate-on-scroll">
        <Container>
          <div className="section-header">
            <Badge className="section-badge">Testimonials</Badge>
            <h2 className="section-title">Loved by teams<br/><span className="gradient-text">around the world</span></h2>
          </div>
          
          <Row>
            {testimonials.map((testimonial, index) => (
              <Col md={4} key={index}>
                <Card className="testimonial-card">
                  <Card.Body>
                    <div className="testimonial-stars">★★★★★</div>
                    <p className="testimonial-quote">"{testimonial.quote}"</p>
                    <div className="testimonial-author">
                      <div className="author-avatar">{testimonial.avatar}</div>
                      <div className="author-info">
                        <strong>{testimonial.author}</strong>
                        <span>{testimonial.role}</span>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="pricing-section animate-on-scroll">
        <Container>
          <div className="section-header">
            <Badge className="section-badge">Pricing</Badge>
            <h2 className="section-title">Simple, transparent<br/><span className="gradient-text">pricing for everyone</span></h2>
            <p className="section-subtitle">Start free, upgrade when you need more</p>
          </div>
          
          <Row className="justify-content-center">
            <Col lg={4} md={6}>
              <Card className="pricing-card">
                <Card.Body>
                  <div className="pricing-header">
                    <h3>Free</h3>
                    <div className="pricing-price">
                      <span className="price">$0</span>
                      <span className="period">/month</span>
                    </div>
                    <p>Perfect for trying out EchoBrief</p>
                  </div>
                  <ul className="pricing-features">
                    <li><span className="check">✓</span> 5 meetings per month</li>
                    <li><span className="check">✓</span> 60 minutes processing</li>
                    <li><span className="check">✓</span> AI transcription</li>
                    <li><span className="check">✓</span> Basic summaries</li>
                    <li><span className="check">✓</span> Action item extraction</li>
                    <li className="disabled"><span className="x">✗</span> AI Chat</li>
                    <li className="disabled"><span className="x">✗</span> Priority processing</li>
                  </ul>
                  <Link to="/register" className="btn-pricing">Get Started</Link>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={4} md={6}>
              <Card className="pricing-card featured">
                <div className="popular-badge">Most Popular</div>
                <Card.Body>
                  <div className="pricing-header">
                    <h3>Pro</h3>
                    <div className="pricing-price">
                      <span className="price">$29</span>
                      <span className="period">/month</span>
                    </div>
                    <p>For professionals and small teams</p>
                  </div>
                  <ul className="pricing-features">
                    <li><span className="check">✓</span> 50 meetings per month</li>
                    <li><span className="check">✓</span> 600 minutes processing</li>
                    <li><span className="check">✓</span> AI transcription</li>
                    <li><span className="check">✓</span> Advanced summaries</li>
                    <li><span className="check">✓</span> Action item extraction</li>
                    <li><span className="check">✓</span> Unlimited AI Chat</li>
                    <li><span className="check">✓</span> Priority processing</li>
                  </ul>
                  <Link to="/register" className="btn-pricing featured">Start Free Trial</Link>
                </Card.Body>
              </Card>
            </Col>
            
            <Col lg={4} md={6}>
              <Card className="pricing-card">
                <Card.Body>
                  <div className="pricing-header">
                    <h3>Enterprise</h3>
                    <div className="pricing-price">
                      <span className="price">$99</span>
                      <span className="period">/month</span>
                    </div>
                    <p>For large teams and organizations</p>
                  </div>
                  <ul className="pricing-features">
                    <li><span className="check">✓</span> 1000 meetings per month</li>
                    <li><span className="check">✓</span> 10,000 minutes processing</li>
                    <li><span className="check">✓</span> Everything in Pro</li>
                    <li><span className="check">✓</span> API access</li>
                    <li><span className="check">✓</span> Custom integrations</li>
                    <li><span className="check">✓</span> Dedicated support</li>
                    <li><span className="check">✓</span> SSO & advanced security</li>
                  </ul>
                  <Link to="/register" className="btn-pricing">Contact Sales</Link>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ Section */}
      <section id="faq" className="faq-section animate-on-scroll">
        <Container>
          <div className="section-header">
            <Badge className="section-badge">FAQ</Badge>
            <h2 className="section-title">Frequently asked<br/><span className="gradient-text">questions</span></h2>
          </div>
          
          <Row className="justify-content-center">
            <Col lg={8}>
              <Accordion className="faq-accordion">
                {faqs.map((faq, index) => (
                  <Accordion.Item eventKey={index.toString()} key={index}>
                    <Accordion.Header>{faq.question}</Accordion.Header>
                    <Accordion.Body>{faq.answer}</Accordion.Body>
                  </Accordion.Item>
                ))}
              </Accordion>
            </Col>
          </Row>
        </Container>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="cta-bg-animation">
          <div className="gradient-orb orb-1"></div>
          <div className="gradient-orb orb-2"></div>
        </div>
        <Container>
          <div className="cta-content">
            <h2>Ready to transform your meetings?</h2>
            <p>Join thousands of professionals who save hours every week with EchoBrief.</p>
            <div className="cta-buttons">
              <Link to="/register" className="btn-primary-cta">
                Start Free Today
                <span className="btn-arrow">→</span>
              </Link>
              <Link to="/login" className="btn-secondary-cta light">
                Sign In
              </Link>
            </div>
            <p className="cta-note">No credit card required • Free forever plan available</p>
          </div>
        </Container>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <Container>
          <Row>
            <Col lg={4} md={6}>
              <div className="footer-brand">
                <div className="logo">
                  <span className="logo-icon">📊</span>
                  <span className="logo-text">EchoBrief</span>
                </div>
                <p>AI-powered meeting intelligence that helps you focus on what matters.</p>
              </div>
            </Col>
            <Col lg={2} md={6}>
              <div className="footer-links">
                <h4>Product</h4>
                <a href="#features">Features</a>
                <a href="#pricing">Pricing</a>
                <a href="#faq">FAQ</a>
                <a href="#">Integrations</a>
              </div>
            </Col>
            <Col lg={2} md={6}>
              <div className="footer-links">
                <h4>Company</h4>
                <a href="#">About</a>
                <a href="#">Blog</a>
                <a href="#">Contact</a>
              </div>
            </Col>
            <Col lg={2} md={6}>
              <div className="footer-links">
                <h4>Legal</h4>
                <Link to="/privacy">Privacy</Link>
                <Link to="/terms">Terms</Link>
                <a href="#">Security</a>
              </div>
            </Col>
          </Row>
          <div className="footer-bottom">
            <p>© 2025 EchoBrief. All rights reserved.</p>
          </div>
        </Container>
      </footer>
    </div>
  );
};

export default LandingPage;
